import React from 'react';
import { TFunction } from 'i18next';
import { Documentation } from 'graphql-common';
import { NavItem, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Icons from '@lib/components/Icons/Icons';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';

const bookmarkStarIcon = () =>
  React.createElement(MaterialIcon, {
    icon: 'bookmark_star',
    symbolsOutlined: true,
  });

type GetDocumentActionsMenuArgs = {
  item: Documentation;
  onDetails: (v: Documentation) => void;
  onVersionNotes: (v: Documentation) => void;
  onViewDocument: (v: Documentation) => void;
  onToggleFavorite?: (v: Documentation) => void;
  hasDetailsUrl: boolean;
  t: TFunction<'translation', undefined>;
};

/**
 * Build the overflow-menu nav items for a single `Documentation` row card
 * (used by `LinkedDocuments` and `DocumentQuestionField`). Items:
 * - **View document** — always present.
 * - **Version notes** — disabled when the current version's notes are empty
 *   or contain only an empty rich-text shell (`hasTextEditorValue` guards it).
 * - **Details** — disabled when `hasDetailsUrl` is false. Pass `true` when the
 *   caller also provides an `onDetails` that actually navigates somewhere.
 * - **Add/Remove favorite** — rendered only when `onToggleFavorite` is
 *   supplied, so callers that don't use favorites simply omit it.
 */
export default function getDocumentActionsMenu({
  item,
  onDetails,
  onVersionNotes,
  onViewDocument,
  onToggleFavorite,
  hasDetailsUrl,
  t,
}: GetDocumentActionsMenuArgs): NavItem<Documentation>[] {
  const items: NavItem<Documentation>[] = [
    {
      id: 'view-document',
      title: t('view-document'),
      onClick: () => onViewDocument(item),
      leftIcon: Icons.description,
      type: NavItemTypeEnum.Secondary,
    },
    {
      id: 'version-notes',
      title: t('version-notes'),
      onClick: () => onVersionNotes(item),
      leftIcon: Icons.pendingActions,
      type: NavItemTypeEnum.Secondary,
      disabled: !hasTextEditorValue(item.currentVersion?.notes),
    },
    {
      id: 'details',
      title: t('details'),
      onClick: () => onDetails(item),
      leftIcon: Icons.view,
      type: NavItemTypeEnum.Secondary,
      disabled: !hasDetailsUrl,
    },
  ];

  if (onToggleFavorite) {
    items.push({
      id: 'toggle-favorite',
      title: item.favorited
        ? t('remove-from-favorites')
        : t('add-to-favorites'),
      onClick: () => onToggleFavorite(item),
      leftIcon: bookmarkStarIcon,
      type: NavItemTypeEnum.Secondary,
    });
  }

  return items;
}
